import React, { useState, useEffect } from "react";
import { getImageUrl } from "../../utils/utils";

// Sub-component for individual photo rendering with isolated loading state and spinner
function PhotoThumbnail({ src, alt, memberId, photoIndex }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
  }, [src, memberId]);

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "240px",
        width: "100%",
        maxWidth: "200px",
        overflow: "hidden",
        border: "1px solid #ffc2dd",
        borderRadius: "8px",
        margin: "10px auto",
        backgroundColor: "#faf5f8",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
      }}
    >
      {/* Loading Spinner Overlay */}
      {loading && !error && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fdf8fa",
            zIndex: 3,
          }}
        >
          <div
            style={{
              width: "36px",
              height: "36px",
              border: "3px solid #ffccd9",
              borderTop: "3px solid #BF3171",
              borderRadius: "50%",
              animation: "photoApprovalSpin 0.75s linear infinite",
            }}
          />
          <span
            style={{
              marginTop: "10px",
              fontSize: "11px",
              color: "#BF3171",
              fontWeight: "600",
              letterSpacing: "0.4px",
            }}
          >
            Loading photo...
          </span>
        </div>
      )}

      {/* Error State */}
      {error ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            color: "#dc3545",
            textAlign: "center",
          }}
        >
          <i
            className="fa fa-triangle-exclamation"
            style={{ fontSize: "28px", marginBottom: "8px", color: "#dc3545" }}
          />
          <span style={{ fontSize: "12px", fontWeight: "600" }}>
            Failed to load
          </span>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm mt-2"
            style={{ fontSize: "10px", padding: "2px 8px" }}
            onClick={() => {
              setError(false);
              setLoading(true);
            }}
          >
            Retry
          </button>
        </div>
      ) : (
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          title="Click to view full image in new tab"
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-in",
          }}
        >
          <img
            key={`${memberId}_${photoIndex}_${src}`}
            src={src}
            alt={alt}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError(true);
            }}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              borderRadius: "6px",
              opacity: loading ? 0 : 1,
              transition: "opacity 0.25s ease-in-out",
            }}
          />
        </a>
      )}
    </div>
  );
}

export default function ViewPhotoPopup({ memberData, isOpen, closePopup, onPhotoStatusChange }) {
  const [photoRemarks, setPhotoRemarks] = useState(["", "", "", "", ""]);
  const [localMemberData, setLocalMemberData] = useState(memberData || {});
  const [actionLoading, setActionLoading] = useState(null); // string like "0_Verified" or "1_Rejected"

  // Keep local member data and remarks in sync whenever memberData prop updates
  useEffect(() => {
    setLocalMemberData(memberData || {});
    const remarks = ["", "", "", "", ""];
    if (memberData?.photoApprovalRemarks && typeof memberData.photoApprovalRemarks === "object") {
      for (let i = 0; i < remarks.length; i += 1) {
        const value = memberData.photoApprovalRemarks[i] ?? memberData.photoApprovalRemarks[String(i)];
        if (typeof value === "string") {
          remarks[i] = value;
        }
      }
    }
    setPhotoRemarks(remarks);
    setActionLoading(null);
  }, [memberData]);

  if (!isOpen) return null; // hide modal if not open

  const API_URL = process.env.REACT_APP_BASE_URL_API || "";
  const token = localStorage.getItem("adminToken");

  const handlePhotoAction = async (photoIndex, status) => {
    const actionKey = `${photoIndex}_${status}`;
    setActionLoading(actionKey);

    try {
      const response = await fetch(
        `${API_URL}/api/admin/profile-management/profile-approval-request`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: localMemberData._id,
            type: "photo",
            status: status,
            photo_index: photoIndex,
            remark: photoRemarks[photoIndex] || "",
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update photo status");
      }

      await response.json();

      const updatedMember = { ...localMemberData };
      const photoFields = [
        { pending: "pending_photo", main: "photo", blur_pending: "pending_photo_blur", blur_main: "photo_blur" },
        { pending: "pending_photo_1", main: "photo1", blur_pending: "pending_photo_1_blur", blur_main: "photo1_blur" },
        { pending: "pending_photo_2", main: "photo2", blur_pending: "pending_photo_2_blur", blur_main: "photo2_blur" },
        { pending: "pending_photo_3", main: "photo3", blur_pending: "pending_photo_3_blur", blur_main: "photo3_blur" },
        { pending: "pending_photo_4", main: "photo4", blur_pending: "pending_photo_4_blur", blur_main: "photo4_blur" },
      ];

      const currentField = photoFields[photoIndex];
      if (status === "Verified") {
        if (updatedMember[currentField.pending] && updatedMember[currentField.pending] !== "null") {
          updatedMember[currentField.main] = updatedMember[currentField.pending];
          updatedMember[currentField.blur_main] = updatedMember[currentField.blur_pending];
        }
      }

      updatedMember[currentField.pending] = null;
      updatedMember[currentField.blur_pending] = null;

      const remarks = { ...(updatedMember.photoApprovalRemarks || {}) };
      if (photoRemarks[photoIndex]) {
        remarks[photoIndex] = photoRemarks[photoIndex].trim();
      }
      updatedMember.photoApprovalRemarks = remarks;

      setLocalMemberData(updatedMember);

      if (onPhotoStatusChange) {
        onPhotoStatusChange();
      }
    } catch (error) {
      console.error("Error updating photo status:", error);
      alert("Failed to update photo status. Please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  const renderPhotoCell = (photoIndex) => {
    const pendingFields = [
      "pending_photo", "pending_photo_1", "pending_photo_2", "pending_photo_3", "pending_photo_4",
    ];
    const mainFields = [
      "photo", "photo1", "photo2", "photo3", "photo4",
    ];

    const pendingPhoto = localMemberData[pendingFields[photoIndex]];
    const mainPhoto = localMemberData[mainFields[photoIndex]];
    const memberId = localMemberData?._id || "temp";

    // 1. If there's a main photo, it's approved
    if (mainPhoto && mainPhoto !== "null") {
      return (
        <div style={{ textAlign: "center" }}>
          <PhotoThumbnail
            src={getImageUrl(mainPhoto)}
            alt={`Photo ${photoIndex + 1} - Approved`}
            memberId={memberId}
            photoIndex={photoIndex}
          />
          <div style={{ color: "#28a745", fontWeight: "bold", fontSize: "13px" }}>
            <i className="fa fa-check me-1" /> Approved
          </div>
          {photoRemarks[photoIndex] ? (
            <div style={{ marginTop: 8, fontSize: 12, color: "#555" }}>
              <strong>Remark:</strong> {photoRemarks[photoIndex]}
            </div>
          ) : null}
        </div>
      );
    }

    // 2. If there's a pending photo, show approve/reject buttons
    if (pendingPhoto && pendingPhoto !== "null") {
      const isVerifying = actionLoading === `${photoIndex}_Verified`;
      const isRejecting = actionLoading === `${photoIndex}_Rejected`;
      const isBusy = actionLoading !== null;

      return (
        <div style={{ textAlign: "center" }}>
          <PhotoThumbnail
            src={getImageUrl(pendingPhoto)}
            alt={`Photo ${photoIndex + 1} - Pending`}
            memberId={memberId}
            photoIndex={photoIndex}
          />

          <div style={{ marginTop: 8, display: "flex", justifyContent: "center", gap: "8px" }}>
            <button
              className="btn btn-success btn-sm"
              disabled={isBusy}
              onClick={() => handlePhotoAction(photoIndex, "Verified")}
              style={{ minWidth: "70px", fontWeight: "600" }}
            >
              {isVerifying ? (
                <>
                  <span
                    style={{
                      display: "inline-block",
                      width: "12px",
                      height: "12px",
                      border: "2px solid #ffffff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "photoApprovalSpin 0.75s linear infinite",
                      marginRight: "4px",
                      verticalAlign: "middle",
                    }}
                  />
                  Wait...
                </>
              ) : (
                "Verify"
              )}
            </button>
            <button
              className="btn btn-danger btn-sm"
              disabled={isBusy}
              onClick={() => handlePhotoAction(photoIndex, "Rejected")}
              style={{ minWidth: "70px", fontWeight: "600" }}
            >
              {isRejecting ? (
                <>
                  <span
                    style={{
                      display: "inline-block",
                      width: "12px",
                      height: "12px",
                      border: "2px solid #ffffff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "photoApprovalSpin 0.75s linear infinite",
                      marginRight: "4px",
                      verticalAlign: "middle",
                    }}
                  />
                  Wait...
                </>
              ) : (
                "Reject"
              )}
            </button>
          </div>

          <div className="form-group" style={{ marginTop: 10 }}>
            <label
              htmlFor={`photoRemark_${photoIndex}`}
              style={{ fontSize: 12, display: "block", marginBottom: 4, color: "#666" }}
            >
              Optional remark
            </label>
            <textarea
              id={`photoRemark_${photoIndex}`}
              className="form-control"
              value={photoRemarks[photoIndex] || ""}
              onChange={(e) => {
                const next = [...photoRemarks];
                next[photoIndex] = e.target.value;
                setPhotoRemarks(next);
              }}
              placeholder="Add remark for this photo action"
              rows={2}
              style={{ resize: "vertical", fontSize: "12px" }}
            />
          </div>

          <div style={{ color: "#e67e22", fontWeight: "bold", marginTop: 5, fontSize: "12px" }}>
            Pending Approval
          </div>
        </div>
      );
    }

    // 3. No photo uploaded
    return (
      <div style={{ textAlign: "center", padding: "40px 10px" }}>
        <p style={{ color: "#aaa", fontSize: "20px", marginBottom: "5px" }}>---</p>
        <div style={{ color: "#999", fontWeight: "600", fontSize: "13px" }}>
          Not Uploaded
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Keyframe animation for spinners */}
      <style>
        {`
          @keyframes photoApprovalSpin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <div
        className="modal fade show"
        tabIndex={-1}
        aria-labelledby="documentModalLabel"
        aria-modal="true"
        style={{
          display: "block",
          backgroundColor: "rgba(0,0,0,0.55)",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 1050,
          overflowY: "auto",
        }}
      >
        <div
          className="modal-dialog modal-xl"
          style={{
            maxWidth: "1100px",
            margin: "40px auto",
            width: "95%",
          }}
        >
          <div className="modal-content" style={{ maxHeight: "90vh", overflowY: "auto" }}>
            <div className="modal-header">
              <h5 className="modal-title" id="documentModalLabel" style={{ fontWeight: 600 }}>
                Photo Management - {localMemberData?.name || "User"} (ID: {localMemberData?.profile_id || "N/A"})
              </h5>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={closePopup}
                style={{ fontSize: "24px", opacity: 0.8 }}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div className="modal-body">
              <div className="alert alert-info" style={{ fontSize: "13px" }}>
                <strong>Photo Status Guide:</strong><br />
                • <span style={{ color: "green", fontWeight: "600" }}>Green ✓ Approved</span> - Photo has been verified and moved to main profile<br />
                <strong>Actions:</strong> Click &quot;Verify&quot; to approve or &quot;Reject&quot; to deny pending photos. Rejected photos will be cleared and users can upload again.
              </div>

              <table
                className="table table-striped table-bordered bulk_action mytable"
                cellPadding={0}
                cellSpacing={0}
                style={{ verticalAlign: "middle" }}
              >
                <thead>
                  <tr style={{ textAlign: "center" }}>
                    <th style={{ width: "20%", minWidth: "180px" }}>Photo 1</th>
                    <th style={{ width: "20%", minWidth: "180px" }}>Photo 2</th>
                    <th style={{ width: "20%", minWidth: "180px" }}>Photo 3</th>
                    <th style={{ width: "20%", minWidth: "180px" }}>Photo 4</th>
                    <th style={{ width: "20%", minWidth: "180px" }}>Photo 5</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ verticalAlign: "top" }}>{renderPhotoCell(0)}</td>
                    <td style={{ verticalAlign: "top" }}>{renderPhotoCell(1)}</td>
                    <td style={{ verticalAlign: "top" }}>{renderPhotoCell(2)}</td>
                    <td style={{ verticalAlign: "top" }}>{renderPhotoCell(3)}</td>
                    <td style={{ verticalAlign: "top" }}>{renderPhotoCell(4)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closePopup}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
